import { useState, useEffect } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Zap, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function QuickQuoteModal({ open, onClose, selectedItems = [] }) {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      Promise.all([api.get("/customers"), api.get("/admin/companies")])
        .then(([c, co]) => { setCustomers(c.data.items || []); setCompanies(co.data || []); })
        .catch(() => {});
      setLines(
        selectedItems.map((item) => ({
          item_code: item.item_code,
          item_name: item.item_name,
          batch: item.recommended_batch || (item.batches?.[0]?.batch_code) || "",
          quantity: 1,
          unit_price: item.base_price || item.batches?.[0]?.price || 0,
          discount_pct: 0,
          company_id: item.company_id,
        }))
      );
    }
  }, [open, selectedItems]);

  const updateQty = (idx, delta) => {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, quantity: Math.max(1, l.quantity + delta) } : l));
  };

  const companyId = lines[0]?.company_id || "";
  const total = lines.reduce((s, l) => s + l.quantity * l.unit_price * (1 - l.discount_pct / 100), 0);

  const handleCreate = async (editFirst = false) => {
    if (!customerId) { toast.error("Select a customer"); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: customerId,
        company_id: companyId,
        type: companies.find((c) => c.id === companyId)?.type === "organic" ? "ORGANIC" : "NON_ORGANIC",
        validity_days: 15,
        lines: lines.map((l) => ({
          item_code: l.item_code,
          item_name: l.item_name,
          batch: l.batch,
          quantity: l.quantity,
          unit_price: l.unit_price,
          discount_pct: l.discount_pct,
        })),
      };
      const res = await api.post("/quotations", payload);
      if (editFirst) {
        toast.success("Quotation created — redirecting to edit");
        navigate(`/quotations/${res.data.id}`);
      } else {
        await api.post(`/quotations/${res.data.id}/submit`);
        toast.success("Quotation created and submitted for approval");
        navigate("/quotations");
      }
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create quotation"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl rounded-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
            <Zap size={18} className="text-[#0052CC]" /> Quick Quote
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="quick-quote-customer" className="mt-1 rounded-sm h-9">
                <SelectValue placeholder="Select customer..." />
              </SelectTrigger>
              <SelectContent>
                {customers.filter((c) => !companyId || c.company_id === companyId).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 mb-2 block">Items</Label>
            <div className="space-y-2">
              {lines.map((line, idx) => (
                <div key={idx} data-testid={`quick-quote-line-${idx}`} className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{line.item_code}</div>
                    <div className="text-xs text-gray-500 truncate">{line.item_name}</div>
                    <div className="text-[10px] text-gray-400">Batch: {line.batch || "Auto"} | Price: {line.unit_price.toFixed(2)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0 rounded-sm" onClick={() => updateQty(idx, -1)}>
                      <Minus size={12} />
                    </Button>
                    <Input
                      type="number"
                      value={line.quantity}
                      onChange={(e) => setLines((p) => p.map((l, i) => i === idx ? { ...l, quantity: Math.max(1, parseInt(e.target.value) || 1) } : l))}
                      className="w-16 h-7 text-center rounded-sm text-sm"
                    />
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0 rounded-sm" onClick={() => updateQty(idx, 1)}>
                      <Plus size={12} />
                    </Button>
                  </div>
                  <div className="text-right w-20">
                    <div className="text-sm font-semibold">{(line.quantity * line.unit_price).toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end border-t pt-3">
            <div className="text-right">
              <span className="text-xs uppercase tracking-wider text-gray-500 mr-3">Total</span>
              <span className="text-lg font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>{total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button
            data-testid="quick-quote-edit"
            variant="outline"
            onClick={() => handleCreate(true)}
            disabled={saving}
            className="rounded-sm"
          >
            Edit First
          </Button>
          <Button
            data-testid="quick-quote-submit"
            onClick={() => handleCreate(false)}
            disabled={saving}
            className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm"
          >
            <Zap size={14} className="mr-1" /> Create & Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
